import { createLogger } from "@/logger";
import { computeTopFeedsByActivity } from "@/models/repositories/Feed";
import { computeUnreadFeedIds } from "@/models/repositories/Item";
import { membershipHasReadOrWrite } from "@/store/feedSelectors";
import { store } from "@/store/store";
import type { WarmupSettings } from "./warmupConstants";

const log = createLogger("warmup");

export type FeedsToWarmup = {
  /** The feeds to fetch, in fetch order, with the ones already fully loaded left out. */
  feedIds: string[];
  unreadCount: number;
  activityCount: number;
  /** How many feeds were added beyond the unread and activity picks: alias feeds on Dispatch, every feed in `all`. */
  aliasCount: number;
  totalUnique: number;
  alreadyLoaded: number;
};

/**
 * Which feeds warmup loads, in the order it loads them.
 *
 * `off` loads none. `limited` takes the feeds with unread items first, newest activity first, then the busiest of the
 * rest, up to `feedLimit`; with `dispatchEnabled`, every alias feed the member can read follows, so a count of each
 * driver's unread messages can be drawn from the store: the Dispatch page's badge needs every channel, not the busiest
 * few. `all` takes the same order with no limit, then every other feed of the workspace the member can read. Feeds
 * whose newest page was fetched since this boot are left out either way: every boot clears `loadedLastPage`, so the
 * flag never carries over from an earlier one.
 */
export function warmupCandidateFeedIds({
  workspaceId,
  workspaceMembershipId,
  settings,
  dispatchEnabled = false,
}: {
  workspaceId: string;
  workspaceMembershipId: string;
  settings: Pick<WarmupSettings, "mode" | "feedLimit">;
  dispatchEnabled?: boolean;
}): FeedsToWarmup {
  const feedIds = new Set<string>();
  let unreadCount = 0;
  let activityCount = 0;
  let aliasCount = 0;

  if (settings.mode === "off") {
    return { feedIds: [], unreadCount: 0, activityCount: 0, aliasCount: 0, totalUnique: 0, alreadyLoaded: 0 };
  }

  const everyFeed = settings.mode === "all";
  const feedLimit = everyFeed ? Number.POSITIVE_INFINITY : settings.feedLimit;

  const unreadFeeds = computeUnreadFeedIds({
    myCurrentWorkspaceMembershipId: workspaceMembershipId,
    requireNotificationPreference: false,
    limit: feedLimit,
  });

  unreadCount = unreadFeeds.length;

  for (const f of unreadFeeds) {
    feedIds.add(f.feedId);
  }

  log.info("[WARMUP] Found feeds with unreads", {
    count: unreadFeeds.length,
    limit: feedLimit,
    feedIds: Array.from(feedIds),
  });

  const remainingSlots = feedLimit - feedIds.size;

  if (remainingSlots > 0) {
    const topFeeds = computeTopFeedsByActivity({
      workspaceId,
      myCurrentWorkspaceMembershipId: workspaceMembershipId,
      limit: feedLimit,
      requireNotificationPreference: false,
    });

    for (const f of topFeeds) {
      if (feedIds.size >= feedLimit) break;
      if (!feedIds.has(f.id)) {
        feedIds.add(f.id);
        activityCount++;
      }
    }

    log.info("[WARMUP] Filled remaining slots from top feeds by activity", {
      remainingSlots,
      activityFeedsAdded: activityCount,
      totalFeeds: feedIds.size,
    });
  }

  if (everyFeed || dispatchEnabled) {
    for (const feed of store.getAll("feed").values()) {
      if (feed.workspaceId !== workspaceId || feedIds.has(feed.id)) continue;
      if (!everyFeed && !feed.isAliasFeed) continue;
      if (!membershipHasReadOrWrite(feed.id, workspaceMembershipId)) continue;
      feedIds.add(feed.id);
      aliasCount++;
    }
    log.info("[WARMUP] Added the remaining readable feeds", {
      everyFeed,
      feedsAdded: aliasCount,
      totalFeeds: feedIds.size,
    });
  }

  const finalFeedIds = Array.from(feedIds);

  // Filter out feeds whose newest page was fetched since this boot
  let alreadyLoadedCount = 0;
  let feedsToWarmup: string[] = finalFeedIds;

  if (finalFeedIds.length > 0) {
    const alreadyLoadedIds = new Set(finalFeedIds.filter((id) => store.get("feed", id)?.loadedLastPage === true));
    alreadyLoadedCount = alreadyLoadedIds.size;
    feedsToWarmup = finalFeedIds.filter((id) => !alreadyLoadedIds.has(id));

    log.info("[WARMUP] Filtered out already loaded feeds", {
      totalCandidates: finalFeedIds.length,
      alreadyLoaded: alreadyLoadedIds.size,
      needsWarmup: feedsToWarmup.length,
    });
  }

  return {
    feedIds: feedsToWarmup,
    unreadCount,
    activityCount,
    aliasCount,
    totalUnique: finalFeedIds.length,
    alreadyLoaded: alreadyLoadedCount,
  };
}
